import { AttributeHistory } from '@/entities/srm/ci';
import { CIType } from '@/entities/srm/ci';
import { Tree } from '@/shared/lib/utils/tree/types';
import { FORMAT } from '@/shared/config';
import { isValidJson } from '@/shared/lib/utils/is-valid-json';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';
import type { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { omit } from 'lodash';
import type { ReactNode } from 'react';

interface Props {
  currentCit: CIType;
  citList: Tree<CIType>;
  ci: {
    id: string;
    ci_name: string;
    label?: ReactNode;
  };
  historyChangeItems?: AttributeHistory[];
  attrLabel: string;
  t: TFunction;
}

export const findNodeRecudcively = (tree, key, compareBy) => {
  for (const node of tree) {
    if (node[key] === compareBy) {
      return node;
    }
    if (node.children) {
      const foundInChildren = findNodeRecudcively(
        node.children,
        key,
        compareBy,
      );
      if (foundInChildren) {
        return foundInChildren;
      }
    }
  }
  return undefined;
};

export const filterTreeRecursively = (filterBy, filterKey, citList = []) => {
  if (!citList?.length) return [];

  return citList
    .map((elem) => {
      if (elem[filterKey] === filterBy) {
        return omit(elem, 'children');
      }

      if (elem.children) {
        const filteredChildren = filterTreeRecursively(
          filterBy,
          filterKey,
          elem.children,
        );
        if (filteredChildren.length > 0) {
          return { ...elem, children: filteredChildren };
        }
      }

      return null;
    })
    .filter((elem) => elem !== null);
};

export const getHistoryCompareId = (ci, attr, historyItem, index) => {
  return (
    historyItem?.compareId ||
    historyItem?.value_id ||
    `${ci.id}:${attr}:${historyItem?.start_date || 'current'}:${index}`
  );
};

export const getAttributeHistoryCompareId = (
  nodeId: string,
  attrId: string,
  historyItem: any,
  index: number,
) => {
  return (
    historyItem?.compareId ||
    historyItem?.value_id ||
    `${nodeId}:${attrId}:${historyItem?.start_date || 'current'}:${index}`
  );
};

export const stringifyAttributeValue = (value: any) => {
  if (typeof value === 'string') {
    return value;
  }

  if (value === null || typeof value === 'undefined') {
    return '';
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const ATTRIBUTE_VALUE_INLINE_MAX_CHARS = 240;

export const stringifyAttributeValueInline = (
  value: any,
  maxChars = ATTRIBUTE_VALUE_INLINE_MAX_CHARS,
) => {
  const text =
    typeof value === 'string' ? value : stringifyAttributeValue(value);
  const slice = text.length > maxChars ? text.slice(0, maxChars) : text;
  const collapsed = slice.replace(/\s+/g, ' ').trim();
  return text.length > maxChars ? `${collapsed}…` : collapsed;
};

export const parseAttributeFormValue = (value: any, schema?: any) => {
  if (
    typeof value === 'string' &&
    ['object', 'array'].includes(schema?.type) &&
    isValidJson(value)
  ) {
    return JSON.parse(value);
  }

  return value;
};

export const formatRevisionDate = (date?: string | null) => {
  return date ? dayjs(date).format(FORMAT.DATE) : '';
};

const addCiAndAttrToTree = (tree, ci, attr, historyChanges, t) => {
  const stack = [...tree];

  while (stack.length > 0) {
    const node = stack.pop();

    if (!node.children || node.children.length === 0) {
      node.children = [
        {
          key: ci.id,
          value: ci.id,
          label: ci.ci_name,
          selectable: false,
          type: 'ci',

          children: [
            {
              value: attr + ci.id,
              label: attr,
              selectable: false,
              type: 'attribute',
              children: historyChanges?.map((el, index) => {
                const compareId = getHistoryCompareId(ci, attr, el, index);

                return {
                  label:
                    el?.end_date === null || !el?.start_date
                      ? t('srm:nodePanel.currentValue')
                      : dayjs(el?.start_date).format(FORMAT.DATE),
                  key: compareId,
                  value: compareId,
                  compareValue: el.value,
                  start_date: el.start_date,
                  end_date: el.end_date,
                  value_id: el.value_id,
                  selectable: true,
                };
              }),
            },
          ],
        },
      ];
    } else {
      stack.push(...node.children);
    }
  }
  return tree;
};

export function createAttrHistoryTree({
  currentCit,
  citList,
  ci,
  historyChangeItems,
  attrLabel,
  t,
}: Props) {
  const citsTreePart = filterTreeRecursively(currentCit?.name, 'name', citList);

  const modifiedTree = treeMap(citsTreePart, 'children', (x) => ({
    label: x.label,
    value: x.name,
    children: x.children,
    selectable: false,
  }));

  const fullTree = addCiAndAttrToTree(
    modifiedTree,
    ci,
    attrLabel,
    historyChangeItems,
    t,
  );

  return fullTree;
}

export const mergeTrees = (trees) => {
  const mergeNodes = (node1, node2, postfixCounter) => {
    if (node1.label !== node2.label) {
      return [node1, node2];
    }

    const children1 = node1.children || [];
    const children2 = node2.children || [];

    const mergedChildren = mergeTreeArrays(
      children1,
      children2,
      postfixCounter,
    );

    return [
      {
        ...node1,
        children: mergedChildren?.sort(
          (a, b) => parseInt(b.label) - parseInt(a.label),
        ),
      },
    ];
  };

  const mergeTreeArrays = (arr1, arr2, postfixCounter) => {
    const merged = [...arr1];

    arr2.forEach((node2) => {
      const existingNodeIndex = merged.findIndex(
        (node1) => node1.label === node2.label,
      );

      if (existingNodeIndex > -1) {
        const existingNode = merged[existingNodeIndex];
        const mergedNodes = mergeNodes(existingNode, node2, postfixCounter);
        merged.splice(existingNodeIndex, 1, ...mergedNodes);
      } else {
        const uniqueNode = { ...node2 };
        if (merged.some((node1) => node1.label === node2.label)) {
          uniqueNode.value += `_${postfixCounter.counter++}`;
        }
        merged.push(uniqueNode);
      }
    });

    return merged;
  };

  if (trees.length === 0) {
    return [];
  }

  const postfixCounter = { counter: 1 };
  let mergedTree = [trees[0]];

  for (let i = 1; i < trees.length; i++) {
    mergedTree = mergeTreeArrays(mergedTree, [trees[i]], postfixCounter);
  }

  return mergedTree;
};

export const modifyTreeData = (tree, t: TFunction) => {
  return treeMap(tree, 'children', (x) => {
    if (x?.type === 'attribute') {
      return {
        ...x,
        labelPostfix: (
          <p className='m-0 text-slate-400 '>
            {t('srm:settingsDialog.attribute')}
          </p>
        ),
      };
    } else if (x?.type === 'ci') {
      return {
        ...x,
        labelPostfix: (
          <p className='m-0 text-slate-400 '>{t('srm:nodePanel.title')}</p>
        ),
      };
    }
    return x;
  });
};